import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMasterPartnerAuth } from '../contexts/MasterPartnerAuthContext';
import { 
  Users, Search, Filter, ChevronRight, Home, FileText, Settings, 
  LogOut, Menu, X, Loader2, Eye, RefreshCw, Building2, Phone, Mail, IndianRupee
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterPartnerPartners = () => {
  const navigate = useNavigate();
  const { masterPartner, logout, getAuthHeader } = useMasterPartnerAuth();
  
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerStats, setPartnerStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, [statusFilter]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/master-partner/portal/partners?${params}`, {
        headers: getAuthHeader()
      });
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerStats = async (partnerId) => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API}/master-partner/portal/partners/${partnerId}/stats`, {
        headers: getAuthHeader()
      });
      setPartnerStats(response.data);
    } catch (error) {
      console.error('Error fetching partner stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleViewPartner = (partner) => {
    setSelectedPartner(partner);
    fetchPartnerStats(partner.id);
  };

  const handleLogout = () => {
    logout();
    navigate('/master-partner/login');
  };

  const filteredPartners = partners.filter(partner => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      partner.company_name?.toLowerCase().includes(query) ||
      partner.contact_person?.toLowerCase().includes(query) ||
      partner.email?.toLowerCase().includes(query) ||
      partner.phone?.includes(query)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'under_review': return 'bg-blue-100 text-blue-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'suspended': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b">
          <h1 className="text-xl font-bold text-gray-900">Master Partner</h1>
          <p className="text-sm text-gray-500 truncate">{masterPartner?.company_name}</p>
        </div>
        
        <nav className="p-4 space-y-2">
          <Link 
            to="/master-partner/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Home className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/master-partner/partners" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-purple-50 text-purple-700 font-medium"
          >
            <Users className="w-5 h-5" />
            My Partners
          </Link>
          <Link 
            to="/master-partner/commissions" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <IndianRupee className="w-5 h-5" />
            Commissions
          </Link>
          <Link 
            to="/master-partner/profile" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-5 h-5" />
            Profile
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1 lg:flex-none">
              <h2 className="text-lg font-semibold text-gray-900">My Partners</h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchPartners}
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search partners..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          </div>

          {/* Partners Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
              </div>
            ) : filteredPartners.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium">No Partners Found</h3>
                <p className="mt-1">
                  {searchQuery || statusFilter 
                    ? 'Try adjusting your filters' 
                    : 'Share your referral link to recruit partners'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Partner</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Leads</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Commission</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredPartners.map((partner) => (
                      <tr key={partner.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{partner.company_name}</p>
                            <p className="text-sm text-gray-500">{partner.contact_person}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <p className="text-gray-900">{partner.email}</p>
                            <p className="text-gray-500">{partner.phone}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(partner.status)}`}>
                            {partner.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <p className="font-medium text-gray-900">{partner.lead_stats?.total || 0} total</p>
                            <p className="text-green-600">{partner.lead_stats?.converted || 0} converted</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">₹{(partner.total_commission || 0).toLocaleString()}</p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleViewPartner(partner)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Partner Detail Modal */}
      {selectedPartner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-900">Partner Details</h3>
              <button
                onClick={() => {
                  setSelectedPartner(null);
                  setPartnerStats(null);
                }}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {loadingStats ? (
                <div className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
                </div>
              ) : partnerStats ? (
                <div className="space-y-6">
                  {/* Partner Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-8 h-8 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">{partnerStats.partner?.company_name}</h4>
                      <p className="text-gray-500">{partnerStats.partner?.contact_person}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="flex items-center gap-1 text-gray-600">
                          <Mail className="w-4 h-4" />
                          {partnerStats.partner?.email}
                        </span>
                        <span className="flex items-center gap-1 text-gray-600">
                          <Phone className="w-4 h-4" />
                          {partnerStats.partner?.phone}
                        </span>
                      </div>
                      <span className={`inline-flex mt-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(partnerStats.partner?.status)}`}>
                        {partnerStats.partner?.status?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{partnerStats.lead_stats?.total || 0}</p>
                      <p className="text-sm text-purple-600">Total Leads</p>
                    </div>
                    {Object.entries(partnerStats.lead_stats?.by_stage || {}).slice(0, 3).map(([stage, count]) => (
                      <div key={stage} className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-700">{count}</p>
                        <p className="text-sm text-gray-600 capitalize">{stage.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>

                  {/* Recent Leads */}
                  {partnerStats.recent_leads?.length > 0 && (
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-3">Recent Leads</h5>
                      <div className="space-y-2">
                        {partnerStats.recent_leads.map((lead) => (
                          <div key={lead.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                            <div>
                              <p className="font-medium text-gray-900">{lead.name}</p>
                              <p className="text-sm text-gray-500">{lead.service_type}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              lead.stage === 'delivered' ? 'bg-green-100 text-green-700' :
                              lead.stage === 'new' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {lead.stage}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">
                  Failed to load partner details
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterPartnerPartners;
